import { useMemo, useState } from "react";
import type { RoomState } from "../types";
import { PhaseTimer } from "./PhaseTimer";

interface Props {
  room: RoomState;
  you: string;
  onVote: (targetId: string) => void;
}

export function VotingScreen({ room, you, onVote }: Props) {
  const me = room.players.find((p) => p.id === you);
  const [picked, setPicked] = useState<string | null>(null);

  // Stable random ordering per round so nobody's drawing always sits first.
  const ordered = useMemo(() => {
    const seed = `${room.code}-${room.round}-${you}`;
    return shuffleBySeed(room.drawings, seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.code, room.round, room.drawings.length]);

  const cast = (id: string) => {
    if (me?.hasVoted) return;
    if (id === you) return;
    setPicked(id);
    onVote(id);
  };

  return (
    <div className="vote">
      <div className="vote-head">
        <h2>Pick the truest little guy</h2>
        <p className="muted">
          Vote for whoever captured the cat best. You can't vote for your own.
        </p>
        <PhaseTimer endsAt={room.phaseEndsAt} label="Voting closes in" />
      </div>

      <div className="vote-stage">
        <div className="vote-reference">
          <div className="muted small">the actual cat</div>
          {room.reference && <img src={room.reference.url} alt="Reference" />}
        </div>

        <div className="vote-grid">
          {ordered.map((d) => {
            const drawer = room.players.find((p) => p.id === d.playerId);
            const isMine = d.playerId === you;
            const isPicked = picked === d.playerId;
            return (
              <button
                key={d.playerId}
                className={`vote-card ${isPicked ? "picked" : ""} ${isMine ? "self" : ""}`}
                onClick={() => cast(d.playerId)}
                disabled={isMine || me?.hasVoted}
              >
                <img src={d.image} alt={`drawing by ${drawer?.name ?? "?"}`} />
                <div className="vote-card-foot">
                  <span>{isMine ? "your drawing" : "tap to vote"}</span>
                  {isPicked && <span className="pill pill-pick">your vote</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function shuffleBySeed<T>(arr: T[], seed: string): T[] {
  let h = 2166136261;
  for (const c of seed) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    const j = (h >>> 0) % (i + 1);
    const tmp = out[i]!;
    out[i] = out[j]!;
    out[j] = tmp;
  }
  return out;
}
